import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Modal from '../Modal';
import { handleApiError } from '../../utils/errorHandler';
import { balancesApi, AgentPayment } from '../../services/agentBalancesService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  payment: AgentPayment | null;
  onSaved: () => void;
}

const toDateInput = (iso: string): string => {
  if (iso && iso.length >= 10 && iso[4] === '-' && iso[7] === '-') return iso.slice(0, 10);
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const ClientLine = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  padding-bottom: 4px;
  border-bottom: 1px solid var(--border-color-light);
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--surface-color)')};
  color: ${p => (p.$variant === 'primary' ? '#fff' : 'var(--text-primary)')};

  &:hover:not(:disabled) {
    background: ${p => (p.$variant === 'primary' ? 'var(--primary-hover)' : 'var(--hover-background)')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const EditPaymentModal: React.FC<Props> = ({ isOpen, onClose, payment, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && payment) {
      setAmount(String(payment.amount));
      setPaymentDate(toDateInput(payment.paymentDate));
      setNote(payment.note ?? '');
      setReference(payment.reference ?? '');
      setSaving(false);
    }
  }, [isOpen, payment]);

  const handleSave = async () => {
    if (!payment) return;
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('הסכום חייב להיות גדול מאפס');
      return;
    }
    if (!paymentDate) {
      toast.error('יש לבחור תאריך תשלום');
      return;
    }

    try {
      setSaving(true);
      await balancesApi.updatePayment(payment.id, {
        amount: amt,
        paymentDate,
        note: note.trim() || null,
        reference: reference.trim() || null,
      });
      toast.success('התשלום עודכן');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בעדכון התשלום'));
    } finally {
      setSaving(false);
    }
  };

  if (!payment) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="עריכת תשלום"
      width="440px"
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={saving}>ביטול</Button>
          <Button type="button" $variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'עדכון'}
          </Button>
        </>
      }
    >
      <Form>
        <ClientLine>{payment.clientName} · {payment.partyTypeName} {payment.partySourceName}</ClientLine>
        <Field>
          <Label>סכום (₪)</Label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
        </Field>
        <Field>
          <Label>תאריך תשלום</Label>
          <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
        </Field>
        <Field>
          <Label>אסמכתא (אופציונלי)</Label>
          <Input type="text" maxLength={100} value={reference} onChange={e => setReference(e.target.value)} />
        </Field>
        <Field>
          <Label>הערה (אופציונלי)</Label>
          <Input type="text" maxLength={500} value={note} onChange={e => setNote(e.target.value)} />
        </Field>
      </Form>
    </Modal>
  );
};

export default EditPaymentModal;
